/**
 * ============================================================
 * System       : CSAF Alert Finance
 * File         : frontend/src/pages/Companies.jsx
 * Description  : Company CRUD + multi-user assignment
 * ============================================================
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  getCompanies, createCompany, updateCompany, deleteCompany,
  getUsers, addUserCompany, removeUserCompany,
} from '../api';
import { useToast } from '../App';
import Modal from '../components/Modal';

function BuildingIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

export default function Companies({ currentUser }) {
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers]   = useState([]);   // non-admin users (full list)
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  const [companyModal, setCompanyModal] = useState(null); // { mode:'add'|'edit', company? }
  const [companyName, setCompanyName]   = useState('');
  const [userModal, setUserModal]       = useState(null); // { companyId, companyName, mode:'add'|'remove', userId? }
  const [addUserId, setAddUserId]       = useState('');
  const [deleteModal, setDeleteModal]   = useState(null);

  const { addToast } = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, uRes] = await Promise.all([getCompanies(), getUsers()]);
      setCompanies(cRes.data);
      setAllUsers(uRes.data.filter(u => u.role !== 'admin'));
    } catch (e) {
      addToast('error', e.response?.data?.detail || 'โหลดข้อมูลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadData(); }, [loadData]);

  // ---- Company CRUD ----
  const openAdd = () => { setCompanyName(''); setCompanyModal({ mode: 'add' }); };
  const openEdit = (c) => { setCompanyName(c.name); setCompanyModal({ mode: 'edit', company: c }); };

  const handleSaveCompany = async () => {
    if (!companyName.trim()) { addToast('error', 'กรุณากรอกชื่อบริษัท'); return; }
    setSaving(true);
    try {
      if (companyModal.mode === 'add') {
        await createCompany(companyName.trim());
        addToast('success', 'เพิ่มบริษัทสำเร็จ');
      } else {
        await updateCompany(companyModal.company.id, companyName.trim());
        addToast('success', 'แก้ไขชื่อบริษัทสำเร็จ');
      }
      setCompanyModal(null);
      loadData();
    } catch (e) {
      addToast('error', e.response?.data?.detail || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async () => {
    setSaving(true);
    try {
      await deleteCompany(deleteModal.id);
      addToast('success', 'ลบบริษัทสำเร็จ');
      setDeleteModal(null);
      loadData();
    } catch (e) {
      addToast('error', e.response?.data?.detail || 'ลบไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  // ---- User assignment ----
  // Users not yet assigned to a specific company (by userId not in company.users)
  const availableFor = (company) => {
    const assignedIds = new Set((company.users || []).map(u => u.id));
    return allUsers.filter(u => !assignedIds.has(u.id));
  };

  const handleAssignUser = async () => {
    if (!addUserId) { addToast('error', 'กรุณาเลือกผู้ใช้'); return; }
    setSaving(true);
    try {
      await addUserCompany(parseInt(addUserId), userModal.companyId);
      addToast('success', 'เพิ่มผู้ใช้สำเร็จ');
      setUserModal(null);
      setAddUserId('');
      loadData();
    } catch (e) {
      addToast('error', e.response?.data?.detail || 'เพิ่มผู้ใช้ไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveUser = async () => {
    setSaving(true);
    try {
      await removeUserCompany(userModal.userId, userModal.companyId);
      addToast('success', 'ยกเลิกการกำหนดสำเร็จ');
      setUserModal(null);
      loadData();
    } catch (e) {
      addToast('error', e.response?.data?.detail || 'เกิดข้อผิดพลาด');
    } finally {
      setSaving(false);
    }
  };

  const totalActiveRecords = companies.reduce((s, c) => s + (c.active_records || 0), 0);
  const totalRecords       = companies.reduce((s, c) => s + (c.total_records  || 0), 0);
  const totalAssigned      = new Set(companies.flatMap(c => (c.users || []).map(u => u.id))).size;
  const isAdmin            = currentUser?.role === 'admin';

  return (
    <div className="space-y-4">
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'บริษัททั้งหมด',   value: companies.length,   color: 'bg-primary text-white' },
          { label: 'รายการทั้งหมด',   value: totalRecords,       color: 'bg-blue-500 text-white' },
          { label: 'รายการค้างชำระ',  value: totalActiveRecords, color: 'bg-warning text-white' },
          { label: 'พนักงานที่กำหนด', value: totalAssigned,      color: 'bg-success text-white' },
        ].map((c) => (
          <div key={c.label} className={`rounded-xl p-4 ${c.color} shadow-sm`}>
            <div className="text-2xl font-bold">{c.value}</div>
            <div className="text-sm opacity-90">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar — admin only */}
      {isAdmin && (
        <div className="flex justify-end">
          <button onClick={openAdd}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors">
            + เพิ่มบริษัท
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : companies.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 text-center py-16 text-gray-400">
          ยังไม่มีบริษัท กด "เพิ่มบริษัท" เพื่อเริ่มต้น
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {companies.map((c) => {
            const assignedUsers = c.users || [];
            const available = availableFor(c);
            return (
              <div key={c.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
                {/* Header */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-light flex items-center justify-center shrink-0">
                    <BuildingIcon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 leading-tight truncate" title={c.name}>{c.name}</div>
                    <div className="flex gap-3 mt-1 text-xs text-gray-500">
                      <span className="text-warning font-medium">{c.active_records} รายการค้าง</span>
                      <span>·</span>
                      <span>{c.total_records} รายการทั้งหมด</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => openEdit(c)} title="แก้ไขชื่อบริษัท"
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-primary-light hover:text-primary transition-colors">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button onClick={() => setDeleteModal(c)} title="ลบบริษัท"
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-danger transition-colors">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-100" />

                {/* Assigned users */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      พนักงานที่เข้าถึงได้ ({assignedUsers.length})
                    </span>
                    {isAdmin && available.length > 0 && (
                      <button
                        onClick={() => { setUserModal({ companyId: c.id, companyName: c.name, mode: 'add' }); setAddUserId(''); }}
                        className="text-xs text-primary hover:underline font-medium flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                        เพิ่มผู้ใช้
                      </button>
                    )}
                  </div>
                  {assignedUsers.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">ยังไม่มีพนักงานที่กำหนด</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {assignedUsers.map((u) => (
                        <div key={u.id}
                          className="flex items-center gap-1.5 bg-primary-light text-primary rounded-full pl-2 pr-1 py-0.5 text-xs font-medium">
                          <div className="w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                            {(u.fname || '')[0]}
                          </div>
                          <span>{u.fname} {u.lname}</span>
                          {isAdmin && (
                            <button
                              onClick={() => setUserModal({ companyId: c.id, companyName: c.name, mode: 'remove', userId: u.id, userName: `${u.fname} ${u.lname}` })}
                              className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-red-100 hover:text-danger transition-colors ml-0.5">
                              <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {available.length === 0 && allUsers.length > 0 && assignedUsers.length === allUsers.length && (
                    <p className="text-xs text-gray-400 mt-1">พนักงานทุกคนถูกกำหนดแล้ว</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit company modal */}
      {companyModal && (
        <Modal
          title={companyModal.mode === 'add' ? 'เพิ่มบริษัท' : 'แก้ไขชื่อบริษัท'}
          onClose={() => setCompanyModal(null)}
          footer={
            <>
              <button onClick={() => setCompanyModal(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">
                ยกเลิก
              </button>
              <button onClick={handleSaveCompany} disabled={saving || !companyName.trim()}
                className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark text-sm font-medium disabled:opacity-60">
                {saving ? 'กำลังบันทึก...' : 'บันทึก'}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อบริษัท *</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveCompany()}
                placeholder="เช่น บริษัท ก จำกัด"
                autoFocus
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            {companyModal.mode === 'edit' && (
              <p className="text-xs text-gray-400">การแก้ไขชื่อจะอัปเดตทุกรายการที่อ้างอิงบริษัทนี้โดยอัตโนมัติ</p>
            )}
          </div>
        </Modal>
      )}

      {/* Add user modal */}
      {userModal?.mode === 'add' && (
        <Modal
          title={`เพิ่มผู้ใช้ — ${userModal.companyName}`}
          onClose={() => setUserModal(null)}
          footer={
            <>
              <button onClick={() => setUserModal(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">
                ยกเลิก
              </button>
              <button onClick={handleAssignUser} disabled={saving || !addUserId}
                className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark text-sm font-medium disabled:opacity-60">
                {saving ? 'กำลังบันทึก...' : 'เพิ่ม'}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              เลือกพนักงานที่ต้องการให้เข้าถึงข้อมูลของ <strong>{userModal.companyName}</strong>
            </p>
            <select
              value={addUserId}
              onChange={(e) => setAddUserId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="">— เลือกพนักงาน —</option>
              {availableFor(companies.find(c => c.id === userModal.companyId) || { users: [] }).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fname} {u.lname} ({u.email})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400">พนักงาน 1 คน สามารถกำหนดได้หลายบริษัท</p>
          </div>
        </Modal>
      )}

      {/* Remove user confirm */}
      {userModal?.mode === 'remove' && (
        <Modal
          title="ยืนยันการยกเลิกการกำหนดบริษัท"
          onClose={() => setUserModal(null)}
          footer={
            <>
              <button onClick={() => setUserModal(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">
                ยกเลิก
              </button>
              <button onClick={handleRemoveUser} disabled={saving}
                className="px-4 py-2 rounded-lg bg-danger text-white hover:bg-red-700 text-sm font-medium disabled:opacity-60">
                {saving ? 'กำลังบันทึก...' : 'ยืนยัน'}
              </button>
            </>
          }
        >
          <p className="text-sm">
            ต้องการนำ <strong>{userModal.userName}</strong> ออกจาก <strong>{userModal.companyName}</strong> ใช่หรือไม่?
          </p>
        </Modal>
      )}

      {/* Delete company confirm */}
      {deleteModal && (
        <Modal
          title="ยืนยันการลบบริษัท"
          onClose={() => setDeleteModal(null)}
          footer={
            <>
              <button onClick={() => setDeleteModal(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">
                ยกเลิก
              </button>
              <button onClick={handleDeleteCompany} disabled={saving}
                className="px-4 py-2 rounded-lg bg-danger text-white hover:bg-red-700 text-sm font-medium disabled:opacity-60">
                {saving ? 'กำลังลบ...' : 'ลบบริษัท'}
              </button>
            </>
          }
        >
          <div className="space-y-2 text-sm">
            <p>คุณต้องการลบ <strong>{deleteModal.name}</strong> ใช่หรือไม่?</p>
            {(deleteModal.total_records > 0 || (deleteModal.users || []).length > 0) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800 space-y-1">
                {deleteModal.total_records > 0 && (
                  <p>• รายการสำรองจ่าย {deleteModal.total_records} รายการจะถูกลบออกจากบริษัทนี้</p>
                )}
                {(deleteModal.users || []).length > 0 && (
                  <p>• พนักงาน {deleteModal.users.length} คนจะถูกยกเลิกการกำหนดบริษัทนี้</p>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
